/**
 * TNT: vanilla's TntBlock, the primed TNT entity it becomes, and the blast.
 *
 * Lighting a charge — flint and steel, a spreading flame, or another blast —
 * *replaces the block* with a primed TNT entity, exactly as vanilla does. The
 * entity pops up out of the cell (0.2 blocks/tick upward, a random 0.01 either
 * way), falls under gravity, stops dead against terrain, and blinks: vanilla
 * alternates the block texture with a brightened copy every 5 ticks
 * (`fuse / 5 % 2`), so 80 ticks of fuse read as 8 flashes. Its hitbox is 0.98
 * wide and 0.98 tall, its origin at the bottom centre, and it never turns back
 * into a block — the countdown ends in a blast.
 *
 * The blast is vanilla's Explosion: a shell of rays over the charge's sphere,
 * each ray carrying a random share of the power, each cell along the way eating
 * energy in proportion to its blast resistance (see blocks.ts). Every cell a
 * ray still reaches with energy left is destroyed, which is why the crater is
 * round but ragged, why stone stops a charge after a block or so, and why
 * obsidian and bedrock shrug a single TNT off. TNT that a blast destroys is
 * primed in turn with vanilla's shorter 10..29 tick fuse, so a stack goes up as
 * a chain reaction.
 *
 * No item entities, so a blast drops nothing: blocks just vanish, like the rest
 * of this engine's block edits. Primed TNT also ignores fluids (vanilla floats
 * it: water drag and bubble columns) and explosion knockback, the two places
 * this drifts from vanilla.
 *
 * DOM-free: main thread only, but kept free of DOM types so it can move to a
 * worker later like fluid.ts.
 */

import { AIR, BLAST_RESISTANCE, collisionOf, FIRE_ID, IS_LAVA, IS_WATERY, NO_BOXES, TNT_ID } from '../blocks';
import { WORLD_HEIGHT } from '../constants';
import { mulberry32 } from '../noise';
import { clipMove, type ShapesAt } from './collide';

/** Game ticks from priming to the blast (vanilla's default `fuse`). */
export const FUSE_TICKS = 80;
/** Fuse a charge gets when a blast destroys it (vanilla: nextInt(80/4) + 80/8). */
const CHAIN_FUSE_MIN = 10;
const CHAIN_FUSE_MAX = 29;
/** Charge power. Vanilla TNT is 4: a crater a few blocks across in dirt. */
export const POWER = 4;
/** Primed TNT hitbox: 0.98 wide, 0.98 tall, origin at the bottom centre. */
const HALF_W = 0.49;
const HEIGHT = 0.98;
/** Velocity a lit charge starts with (vanilla's primed TNT pops out of its cell). */
const POP_UP = 0.2;
const POP_SIDE = 0.01;
/** Entity physics, vanilla's numbers: gravity, drag, and ground friction. */
const GRAVITY = 0.04;
const DRAG = 0.98;
const GROUND_XZ = 0.7;
const GROUND_Y = -0.5;
/** Vanilla puts the explosion 0.0625 * 0.98 blocks above the entity's feet. */
const EXPLOSION_OFFSET_Y = 0.06125;
/** Ticks the texture holds each phase of the flash (vanilla: `fuse / 5 % 2`). */
const FLASH_TICKS = 5;
/** Rays are the shell of a 16^3 lattice, as vanilla casts its explosion. */
const RAY_LATTICE = 16;
/** Vanilla's per-step constants: energy spent per cell, and per march. */
const STEP = 0.3;
const FALLOFF = 0.225;
/** What vanilla gives water and lava: a blast does not drain them, and a ray
 *  dies in the first wet cell (which is why water shields a blast). */
const FLUID_RESISTANCE = 100;
/** Hair's width, so a box resting exactly on a block face still counts as touching. */
const EPS = 1e-4;
/** Axis order vanilla resolves a move in: up/down, then sideways. */
const FLY_ORDER = [1, 0, 2];

export interface TntHost {
  getBlock(x: number, y: number, z: number): number;
  /** Is the cell inside the world and in a chunk that can be edited right now? */
  canEdit(x: number, y: number, z: number): boolean;
  /** Must report the change back through `TntSim.blockChanged`. */
  setBlock(x: number, y: number, z: number, id: number): void;
}

/** A primed charge: position is the bottom centre of its 0.98 cube. */
export interface Charge {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Ticks left before the blast. */
  fuse: number;
  onGround: boolean;
}

/** Does a charge with `fuse` ticks left draw its brightened texture? Vanilla
 *  alternates the texture every 5 ticks (a lit charge starts lit). */
export function isFlashing(fuse: number): boolean {
  return (Math.floor(fuse / FLASH_TICKS) & 1) === 0;
}

/** 6-neighbourhood, used to notice a flame landing beside a charge. */
const DX = [1, -1, 0, 0, 0, 0];
const DY = [0, 0, 1, -1, 0, 0];
const DZ = [0, 0, 0, 0, 1, -1];

export class TntSim {
  /** Called once per blast with its centre and power, for the effects. */
  onExplode: ((x: number, y: number, z: number, power: number) => void) | null = null;

  /** Primed charges, oldest first. */
  readonly charges: Charge[] = [];
  /** Cells the blast in progress has destroyed (flat x, y, z triples). */
  private readonly cells: number[] = [];
  private readonly doomed = new Set<number>();
  private readonly rnd = mulberry32(0x74d7);

  constructor(private readonly host: TntHost) {}

  /** Charges in flight. */
  get pending(): number {
    return this.charges.length;
  }

  /** Visit every primed charge — the renderer draws them, the fuse sparks off them. */
  forEachCharge(fn: (charge: Charge) => void): void {
    for (const c of this.charges) fn(c);
  }

  /**
   * Light the charge in cell (x, y, z): the block goes, and a primed TNT entity
   * takes its place with vanilla's fuse, position and initial velocity.
   */
  prime(x: number, y: number, z: number, fuse = FUSE_TICKS): void {
    if (this.host.getBlock(x, y, z) !== TNT_ID) return;
    // the block is consumed by the entity that replaces it, so the cell is
    // already empty (and already dark) while the fuse burns
    this.host.setBlock(x, y, z, AIR);
    this.charges.push({
      x: x + 0.5,
      y,
      z: z + 0.5,
      vx: (this.rnd() * 2 - 1) * POP_SIDE,
      vy: POP_UP,
      vz: (this.rnd() * 2 - 1) * POP_SIDE,
      fuse,
      onGround: false,
    });
  }

  /**
   * A block changed: fire next to a charge lights it, and a charge placed
   * against a burning block catches the same way — vanilla TNT is activated by
   * fire as well as by flint and steel.
   */
  blockChanged(x: number, y: number, z: number): void {
    const id = this.host.getBlock(x, y, z);
    if (id === FIRE_ID) {
      for (let d = 0; d < 6; d++) {
        if (this.host.getBlock(x + DX[d], y + DY[d], z + DZ[d]) === TNT_ID) {
          this.prime(x + DX[d], y + DY[d], z + DZ[d]);
        }
      }
    } else if (id === TNT_ID) {
      for (let d = 0; d < 6; d++) {
        if (this.host.getBlock(x + DX[d], y + DY[d], z + DZ[d]) === FIRE_ID) {
          this.prime(x, y, z);
          return;
        }
      }
    }
  }

  /** One 20 Hz game tick: fly every charge, burn its fuse down, blow it up. */
  tick(): void {
    if (this.charges.length === 0) return;
    for (let i = this.charges.length - 1; i >= 0; i--) {
      const c = this.charges[i];
      // frozen (fuse too) while its chunk is not loaded, like every other entity
      if (!this.host.canEdit(Math.floor(c.x), 0, Math.floor(c.z))) continue;
      this.fly(c);
      if (--c.fuse > 0) continue;
      this.charges.splice(i, 1);
      this.explode(c.x, c.y + EXPLOSION_OFFSET_Y, c.z, POWER);
    }
  }

  /**
   * Detonate at (x, y, z) right now with the given power (creepers use 3,
   * TNT 4): the same ray-cast blast a fuse ends in, fires `onExplode`, and
   * chain-primes any TNT it destroys. Blocks only — no entity damage.
   */
  blast(x: number, y: number, z: number, power: number = POWER): void {
    this.explode(x, y, z, power);
  }

  /** Gravity, collision and drag on one charge: vanilla's PrimedTnt.tick. */
  private fly(c: Charge): void {
    c.vy -= GRAVITY;
    c.onGround = false;
    // vanilla Entity.move resolves Y, then X, then Z, zeroing whichever
    // component runs into terrain; a downward clip is what ground means
    const box = [c.x - HALF_W, c.y, c.z - HALF_W, c.x + HALF_W, c.y + HEIGHT, c.z + HALF_W];
    for (let n = 0; n < 3; n++) {
      const axis = FLY_ORDER[n];
      const want = axis === 0 ? c.vx : axis === 1 ? c.vy : c.vz;
      const moved = clipMove(box, axis, want, EPS, this.shapesAt);
      if (moved !== want) {
        if (axis === 0) c.vx = 0;
        else if (axis === 1) { c.onGround = want < 0; c.vy = 0; }
        else c.vz = 0;
      }
      box[axis] += moved;
      box[axis + 3] += moved;
    }
    c.x = (box[0] + box[3]) / 2;
    c.y = box[1];
    c.z = (box[2] + box[5]) / 2;
    c.vx *= DRAG;
    c.vy *= DRAG;
    c.vz *= DRAG;
    if (c.onGround) {
      c.vx *= GROUND_XZ;
      c.vy *= GROUND_Y;
      c.vz *= GROUND_XZ;
    }
  }

  private readonly shapesAt: ShapesAt = (x, y, z) =>
    y < 0 || y >= WORLD_HEIGHT ? NO_BOXES : collisionOf(this.host.getBlock(x, y, z));

  /**
   * Blow the charge at (cx, cy, cz): cast vanilla's rays, destroy what they
   * reach, and chain-light the TNT among it.
   */
  private explode(cx: number, cy: number, cz: number, power: number): void {
    const host = this.host;
    const cells = this.cells;
    const doomed = this.doomed;
    cells.length = 0;
    doomed.clear();

    const last = RAY_LATTICE - 1;
    for (let i = 0; i < RAY_LATTICE; i++) {
      for (let j = 0; j < RAY_LATTICE; j++) {
        for (let k = 0; k < RAY_LATTICE; k++) {
          // only the shell of the lattice: the interior points at a direction
          // an inner ray already covers
          if (i !== 0 && i !== last && j !== 0 && j !== last && k !== 0 && k !== last) continue;
          let dx = (i / last) * 2 - 1;
          let dy = (j / last) * 2 - 1;
          let dz = (k / last) * 2 - 1;
          const mag = Math.hypot(dx, dy, dz);
          dx /= mag;
          dy /= mag;
          dz /= mag;

          // each ray carries its own share of the charge (vanilla: 0.7..1.3x)
          let energy = power * (0.7 + this.rnd() * 0.6);
          let dist = 0;
          while (energy > 0) {
            const bx = Math.floor(cx + dx * dist);
            const by = Math.floor(cy + dy * dist);
            const bz = Math.floor(cz + dz * dist);
            // out of the world / not loaded: this ray stops (vanilla breaks)
            if (!host.canEdit(bx, by, bz)) break;
            const id = host.getBlock(bx, by, bz);
            if (id !== AIR) energy -= (resistanceOf(id) + 0.3) * STEP;
            if (energy > 0 && id !== AIR) {
              const key = cellKey(bx, by, bz);
              if (!doomed.has(key)) {
                doomed.add(key);
                cells.push(bx, by, bz);
              }
            }
            dist += STEP;
            energy -= FALLOFF;
          }
        }
      }
    }

    for (let i = 0; i < cells.length; i += 3) {
      const x = cells[i];
      const y = cells[i + 1];
      const z = cells[i + 2];
      if (host.getBlock(x, y, z) === TNT_ID) {
        // vanilla: TNT a blast destroys is primed with a 10..29 tick fuse
        this.prime(x, y, z, CHAIN_FUSE_MIN + ((this.rnd() * (CHAIN_FUSE_MAX - CHAIN_FUSE_MIN + 1)) | 0));
        continue;
      }
      host.setBlock(x, y, z, AIR);
    }
    this.onExplode?.(cx, cy, cz, power);
  }
}

/** What an explosion ray loses crossing one cell of `id`. */
function resistanceOf(id: number): number {
  if (IS_WATERY[id] === 1 || IS_LAVA[id] === 1) return FLUID_RESISTANCE;
  return BLAST_RESISTANCE[id];
}

/** Integer key of a block cell (|x|, |z| < 2^20, y < 256). */
function cellKey(x: number, y: number, z: number): number {
  return ((x + 0x100000) * 0x200000 + (z + 0x100000)) * 256 + y;
}
